import React, { useState, lazy } from 'react';
import { StarIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Testimonial {
  name: string;
  age: number;
  city: string;
  text: string;
  gender: 'male' | 'female';
  photo: string;
}
const testimonials: Testimonial[] = [{
  name: 'María Fernanda López',
  age: 42,
  city: 'Córdoba',
  gender: 'female',
  photo: 'https://randomuser.me/api/portraits/women/44.jpg',
  text: 'Llevo 15 años dando clases de Seguridad e Higiene y cada año era lo mismo: semanas armando programas, buscando casos nuevos, preparando evaluaciones. Este manual me cambió todo. Ahora tengo todo organizado y puedo dedicar mi tiempo a lo que realmente importa: enseñar.'
}, {
  name: 'Carlos Alberto Ruiz',
  age: 38,
  city: 'Rosario',
  gender: 'male',
  photo: 'https://randomuser.me/api/portraits/men/32.jpg',
  text: 'Empecé a dar clases hace 3 años y me sentía perdido con la planificación. No sabía por dónde empezar. El manual me dio una estructura clara y profesional. Mis alumnos notan la diferencia y yo ya no paso noches armando material.'
}, {
  name: 'Luciana Martínez',
  age: 35,
  city: 'Buenos Aires',
  gender: 'female',
  photo: 'https://randomuser.me/api/portraits/women/31.jpg',
  text: 'Entre el trabajo, la familia y las clases, no me daba el tiempo para preparar todo como quería. Los casos profesionales del manual son exactamente lo que necesitaba. Reales, aplicables y listos para usar. Una inversión que se paga sola.'
}, {
  name: 'Jorge Raúl Pereyra',
  age: 50,
  city: 'Mendoza',
  gender: 'male',
  photo: 'https://randomuser.me/api/portraits/men/55.jpg',
  text: 'Con 25 años de experiencia en el rubro, pensé que no necesitaba ayuda. Pero este manual me sorprendió. La organización progresiva por año y las rúbricas técnicas son de un nivel profesional que no había visto. Lo recomiendo a todos mis colegas.'
}, {
  name: 'Valentina Sosa',
  age: 29,
  city: 'Tucumán',
  gender: 'female',
  photo: 'https://randomuser.me/api/portraits/women/22.jpg',
  text: 'Recién egresada y con mi primer cargo docente, estaba aterrada de no estar a la altura. El manual me dio la confianza que necesitaba. Tengo todo el año planificado, evaluaciones profesionales y casos que impresionan a mis alumnos. ¡Gracias!'
}];
interface UserReview {
  id: string;
  name: string;
  text: string;
  rating: number;
  date: Date;
}
function StarRating({
  rating = 5
}: {
  rating?: number;
}) {
  return <div className="flex gap-1 mb-3" data-id="element-201">
      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-5 h-5 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`} data-id="element-202" />)}
    </div>;
}
export function TestimonialSection() {
  const [userReviews, setUserReviews] = useState<UserReview[]>([]);
  const [formName, setFormName] = useState('');
  const [formText, setFormText] = useState('');
  const [formRating, setFormRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [nameError, setNameError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      setNameError(true);
      return;
    }
    const newReview: UserReview = {
      id: Date.now().toString(),
      name: formName.trim(),
      text: formText.trim(),
      rating: formRating,
      date: new Date()
    };
    setUserReviews([...userReviews, newReview]);
    setFormName('');
    setFormText('');
    setFormRating(5);
    setNameError(false);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
    }, 3000);
  };
  return <section className="py-8 px-4 bg-gray-50" data-id="element-203">
      <div className="max-w-4xl mx-auto" data-id="element-204">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-8" data-id="element-205">
          Lo que dicen otros docentes que probaron el material
        </h2>
        <div className="space-y-5 mb-10" data-id="element-206">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-white rounded-xl p-5 shadow-md border border-gray-100" data-id="element-207">
              <StarRating data-id="element-208" />
              <p className="text-gray-700 text-lg italic mb-4" data-id="element-209">
                "{testimonial.text}"
              </p>
              <div className="flex items-center gap-3" data-id="element-210">
                <img src={testimonial.photo} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover border-2 border-yellow-400 flex-shrink-0" loading="lazy" decoding="async" data-id="element-211" />
                <div data-id="element-212">
                  <p className="font-semibold text-gray-900" data-id="element-213">
                    {testimonial.name}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-214">
                    {testimonial.age} años, {testimonial.city}
                  </p>
                </div>
              </div>
            </div>)}

          <AnimatePresence data-id="element-215">
            {userReviews.map(review => <motion.div key={review.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white rounded-xl p-5 shadow-md border border-gray-100" data-id="element-216">
                <StarRating rating={review.rating} data-id="element-217" />
                {review.text && <p className="text-gray-700 text-lg italic mb-4" data-id="element-218">
                    "{review.text}"
                  </p>}
                <div className="flex items-center gap-3" data-id="element-219">
                  <div className="w-12 h-12 rounded-full bg-green-100 border-2 border-green-500 flex items-center justify-center text-green-700 font-bold text-xl flex-shrink-0" data-id="element-220">
                    {review.name.charAt(0).toUpperCase()}
                  </div>
                  <div data-id="element-221">
                    <p className="font-semibold text-gray-900" data-id="element-222">{review.name}</p>
                    <p className="text-sm text-gray-500" data-id="element-223">Hace un momento</p>
                  </div>
                </div>
              </motion.div>)}
          </AnimatePresence>
        </div>

        {/* Review Form */}
        <div className="bg-white rounded-xl p-6 md:p-8 shadow-lg border-t-4 border-green-500 max-w-2xl mx-auto" data-id="element-224">
          <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-225">
            ✍️ Dejá tu reseña
          </h3>
          <p className="text-gray-600 mb-6" data-id="element-226">
            Compartí tu experiencia con otros docentes
          </p>

          <AnimatePresence mode="wait" data-id="element-227">
            {submitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 text-center font-medium" data-id="element-228">
                ¡Gracias por tu reseña!
              </motion.div> : <motion.form initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onSubmit={handleSubmit} className="space-y-5" data-id="element-229">
                <div data-id="element-230">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-231">
                    Tu calificación
                  </label>
                  <div className="flex gap-1" data-id="element-232">
                    {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setFormRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="focus:outline-none transition-transform hover:scale-110" data-id="element-233">
                        <StarIcon className={`w-8 h-8 ${star <= (hoverRating || formRating) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-100 text-gray-300'} transition-colors`} data-id="element-234" />
                      </button>)}
                  </div>
                </div>

                <div data-id="element-235">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-236">
                    Tu nombre *
                  </label>
                  <input type="text" id="name" value={formName} onChange={e => {
                setFormName(e.target.value);
                if (e.target.value.trim()) setNameError(false);
              }} placeholder="Ej: María López" className={`w-full border ${nameError ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-shadow`} data-id="element-237" />
                  {nameError && <p className="text-red-500 text-sm mt-1" data-id="element-238">
                      El nombre es obligatorio para enviar tu reseña
                    </p>}
                </div>

                <div data-id="element-239">
                  <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-240">
                    Tu comentario
                  </label>
                  <textarea id="comment" value={formText} onChange={e => setFormText(e.target.value)} placeholder="Contanos cómo te ayudó el manual..." rows={4} className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-shadow resize-y" data-id="element-241" />
                </div>

                <button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors shadow-sm" data-id="element-242">
                  Enviar reseña
                </button>
              </motion.form>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}